import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Headline from '../../ui/Headline';
import Button from '../../ui/Button';
import ButtonsGroup from '../../ui/ButtonsGroup';
import ChevronIcon from '../../assets/icons/chevron.svg';
import PenIcon from '../../assets/icons/pen.svg';
import DeleteIcon from '../../assets/icons/delete.svg';
import MissingTranslationsPill from './MissingTranslationsPill';
import { APP_LANGUAGES } from '../../app.config.vars/languages';
import { formatLangCode } from '../../helpers';
// Phase 6 D-11 + D-14 + D-20 + UI-SPEC § "Row anatomy (FaqItemRow)":
//
// A single item row, rendered inside an expanded category's items list. Same
// chrome family as FaqCategoryRow (40px order-arrow column + content column +
// max-content actions column) but:
//   * No chevron toggle — items are not themselves expandable; Edit replaces
//     the row in place with FaqItemEditForm (D-12, handled by FaqItemsSection).
//   * Title is a Headline h3 (smaller than category's h2).
//   * Internal-title slot below the question shows a 60-char plain-text
//     preview of the answer in the current i18n language. If no localized
//     answer exists, the preview falls back to a localized
//     "no translation" italic line per UI-SPEC.
//   * MissingTranslationsPill uses a composite predicate — an item's
//     non-DE language is "missing" when EITHER question or answer is
//     unavailable (D-20). DE is never reported as missing.
//
// Forwarded ref attaches to the outer div so the parent <Animate> wrapper can
// capture this row's position before a ↑↓ swap (FLIP).
const PREVIEW_MAX = 60;
const stripAnswerToPreview = (rawHtml) => {
    if (!rawHtml)
        return '';
    return rawHtml
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
};
const FaqItemRow = forwardRef(({ item, onEdit, onDelete, onMove, disableUp, disableDown }, ref) => {
    const { i18n, t } = useTranslation();
    // WR-04: normalize i18n.language to the supported union ('de'|'en'|'cs').
    // Without this the raw `i18n.language` could be a region-qualified tag
    // (e.g. 'en-US') and the unchecked cast lies to TypeScript — at runtime
    // the downstream `translations[currentLang]` lookup silently returns
    // undefined. formatLangCode strips the region; the APP_LANGUAGES guard
    // falls back to 'de' for any tag we do not support.
    const rawLang = formatLangCode(i18n.language || 'de');
    const currentLang = (APP_LANGUAGES.some((l) => l.code === rawLang)
        ? rawLang
        : 'de');
    const currentLangTranslation = item.translations[currentLang];
    const fallbackTranslation = item.translations.de;
    const displayQuestion = currentLangTranslation?.question || fallbackTranslation?.question || '';
    const plain = stripAnswerToPreview(currentLangTranslation?.answer);
    const hasPreview = plain.length > 0;
    const previewText = hasPreview
        ? plain.length > PREVIEW_MAX
            ? `${plain.slice(0, PREVIEW_MAX).trim()}…`
            : plain
        : t('admin.faq.no_translation_in_current_language');
    // D-20 composite predicate: an item's translation is "missing" when the
    // server's `available` flag is false OR either of the two content fields
    // is empty (defensive — the server's `available` already encodes this,
    // but the explicit predicate makes the row-render contract greppable).
    const isMissing = (lang) => {
        const tr = item.translations[lang];
        if (!tr?.available)
            return true;
        if (!tr.question || !tr.answer)
            return true;
        return false;
    };
    return (_jsx("div", { className: "faq-item-row", ref: ref, children: _jsxs("div", { className: "faq-item-row-chrome", children: [_jsxs("div", { className: "task-order-controls", children: [_jsx("button", { className: "order-arrow arrow-up", onClick: () => onMove('up'), disabled: disableUp, tabIndex: disableUp ? -1 : 0, children: _jsx(ChevronIcon, {}) }), _jsx("button", { className: "order-arrow arrow-down", onClick: () => onMove('down'), disabled: disableDown, tabIndex: disableDown ? -1 : 0, children: _jsx(ChevronIcon, {}) })] }), _jsxs("div", { className: "card-content", children: [_jsx("div", { className: "card-content-column", children: _jsxs("div", { className: "faq-item-title", children: [_jsx(Headline, { title: displayQuestion, type: "h3" }), _jsx("div", { className: `faq-item-answer-preview${hasPreview ? '' : ' empty'}`, children: previewText })] }) }), _jsxs("div", { className: "card-content-column", children: [_jsx(MissingTranslationsPill, { translations: item.translations, isMissing: isMissing }), _jsxs(ButtonsGroup, { children: [_jsx("div", { className: "faq-item-edit-button", children: _jsx(Button, { variant: "outlined", shape: "square", icon: _jsx(PenIcon, {}), manageIcon: "fill", onClick: onEdit }) }), _jsx("div", { className: "faq-item-delete-button", children: _jsx(Button, { variant: "outlined", shape: "square", icon: _jsx(DeleteIcon, {}), manageIcon: "fill", onClick: onDelete }) })] })] })] })] }) }));
});
FaqItemRow.displayName = 'FaqItemRow';
export default FaqItemRow;
